import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {Array<{ img: HTMLImageElement; listener: () => void } | ReturnType<typeof setTimeout>>}
   */
  #submenuImageLoadCleanup = [];

  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#cleanupSubmenuImageLoadListeners();
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const isCompactSubmenu = (el) =>
      el?.dataset?.compactDropdown === 'true' || el?.dataset?.submenu === 'motoring-blog';

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Page images: set an initial height based on current content so first paint doesn't crop
      if (submenu.querySelector('.mega-menu__page-images-grid')) {
        const initialH = Math.max(submenu.offsetHeight || 0, submenu.scrollHeight || 0);
        if (initialH > 0) {
          this.headerComponent?.style.setProperty('--submenu-height', `${initialH}px`);
          this.#setFullOpenHeaderHeight(initialH);
        }
      }

      // Compact / Motoring Blog: set non-zero height immediately so panel is visible before layout; re-measured after visible
      if (isCompactSubmenu(submenu)) {
        const safeH = Math.max(submenu.scrollHeight || 0, 80);
        this.headerComponent?.style.setProperty('--submenu-height', `${safeH}px`);
        this.#setFullOpenHeaderHeight(safeH);
      }

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const hasPageImages = submenu.querySelector('.mega-menu__page-images-grid');
            const isCompact = isCompactSubmenu(submenu);
            const measurable = submenu.offsetHeight > 0 || hasPageImages || isCompact;
            if (measurable) {
              let h = hasPageImages
                ? Math.max(submenu.offsetHeight, submenu.scrollHeight)
                : (isCompact
                  ? Math.max(submenu.offsetHeight || 0, submenu.scrollHeight || 0, 80)
                  : submenu.offsetHeight);
              this.headerComponent?.style.setProperty('--submenu-height', `${h}px`);
              this.#setFullOpenHeaderHeight(h);
              if (!isCompact) this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    // Page images grid: use full content height (scrollHeight) so never crop
    if (submenu?.querySelector('.mega-menu__page-images-grid')) {
      const contentH = Math.max(submenu?.offsetHeight ?? 0, submenu?.scrollHeight ?? 0);
      finalHeight = Math.max(finalHeight, contentH);
    }

    // Compact / Motoring Blog: always use non-zero height so panel is never clipped
    if (submenu && isCompactSubmenu(submenu)) {
      finalHeight = Math.max(finalHeight, submenu.scrollHeight || 0, 80);
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');

    // Page images: re-measure using scrollHeight so full content visible
    if (submenu?.querySelector('.mega-menu__page-images-grid')) {
      const applyHeight = () => {
        if (this.#state.activeItem && findSubmenu(this.#state.activeItem) === submenu) {
          const h = Math.max(submenu.offsetHeight, submenu.scrollHeight);
          this.headerComponent?.style.setProperty('--submenu-height', `${h}px`);
          this.#setFullOpenHeaderHeight(h);
        }
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(applyHeight);
      });
      setTimeout(applyHeight, 50);
      setTimeout(applyHeight, 150);
    }

    // Compact / Motoring Blog: re-measure after submenu is visible so height is correct (offsetHeight may be 0 before layout)
    if (submenu && isCompactSubmenu(submenu)) {
      const applyCompactHeight = () => {
        if (this.#state.activeItem && findSubmenu(this.#state.activeItem) === submenu) {
          const h = Math.max(submenu.offsetHeight || 0, submenu.scrollHeight || 0, 80);
          this.headerComponent?.style.setProperty('--submenu-height', `${h}px`);
          this.#setFullOpenHeaderHeight(h);
        }
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(applyCompactHeight);
      });
      setTimeout(applyCompactHeight, 50);
      setTimeout(applyCompactHeight, 150);
    }

    // Re-measure when submenu content changes (images load, layout settles) so second row is not cut off
    if (submenu && isDefaultSlot) {
      this.#cleanupSubmenuImageLoadListeners();
      this.#submenuResizeObserver?.disconnect();
      const updateHeight = () => {
        if (this.#state.activeItem && findSubmenu(this.#state.activeItem) !== submenu) return;
        const isPageImages = submenu.querySelector('.mega-menu__page-images-grid');
        const isCompact = isCompactSubmenu(submenu);
        const hasHeight = submenu.offsetHeight > 0 || submenu.scrollHeight > 0;
        if (!hasHeight && !isPageImages && !isCompact) return;
        const h = isPageImages
          ? Math.max(submenu.offsetHeight, submenu.scrollHeight)
          : (isCompact
            ? Math.max(submenu.offsetHeight || 0, submenu.scrollHeight || 0, 80)
            : submenu.offsetHeight);
        this.headerComponent?.style.setProperty('--submenu-height', `${h}px`);
        this.#setFullOpenHeaderHeight(h);
      };
      // ResizeObserver: update height whenever the submenu element's size changes (e.g. images loaded)
      this.#submenuResizeObserver = new ResizeObserver(() => updateHeight());
      this.#submenuResizeObserver.observe(submenu);
      // Image load listeners
      const imgs = submenu.querySelectorAll('img');
      imgs.forEach((img) => {
        const listener = () => updateHeight();
        img.addEventListener('load', listener, { once: true });
        this.#submenuImageLoadCleanup.push({ img, listener: () => img.removeEventListener('load', listener) });
      });
      // Delayed re-measures for cached images and slow layout (incl. first-hover)
      [50, 100, 400, 800, 1200].forEach((delay) => {
        this.#submenuImageLoadCleanup.push(setTimeout(updateHeight, delay));
      });
    }
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.#cleanupSubmenuImageLoadListeners();
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  #cleanupSubmenuImageLoadListeners() {
    this.#submenuImageLoadCleanup.forEach((entry) => {
      if (typeof entry === 'number') {
        clearTimeout(entry);
      } else if (entry?.listener) {
        entry.listener();
      }
    });
    this.#submenuImageLoadCleanup = [];
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
